import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Clock, MessageCircle, Calendar, Stethoscope, CheckCircle2 } from "lucide-react";

const STEPS = [
  {key:"NEW", label:"Solicitação recebida", desc:"Recebemos seu pedido de contato e já encaminhamos ao profissional.", icon:CheckCircle2},
  {key:"RECEIVED", label:"Encaminhado ao profissional", desc:"O profissional recebeu seus dados e vai analisar.", icon:Stethoscope},
  {key:"CONTACTED", label:"Contato realizado", desc:"O profissional entrou em contato com você.", icon:MessageCircle},
  {key:"SCHEDULED", label:"Agendamento", desc:"Seu atendimento foi agendado.", icon:Calendar},
  {key:"ATTENDED", label:"Atendimento realizado", desc:"O atendimento foi concluído.", icon:Check},
  {key:"CONVERTED", label:"Finalizado", desc:"Solicitação encerrada com sucesso.", icon:CheckCircle2},
];

export default function PedidoStatus() {
  const { numero } = useParams();
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(()=>{
    api.get(`/leads/by-number/${numero}`).then(r=>setLead(r.data)).catch(e=>setErr("Solicitação não encontrada"));
  }, [numero]);

  if (err) return <PublicLayout><div className="max-w-2xl mx-auto p-10 text-center text-slate-500" data-testid="pedido-not-found">{err}</div></PublicLayout>;
  if (!lead) return <PublicLayout><div className="max-w-2xl mx-auto p-10">Carregando…</div></PublicLayout>;

  const currentIdx = Math.max(0, STEPS.findIndex(s=>s.key===lead.status));

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold">Guia de encaminhamento</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}} data-testid="pedido-title">Solicitação #{lead.number}</h1>
        <p className="text-slate-600 mt-2">Acompanhe cada etapa do seu pedido de contato com transparência.</p>

        <Card className="p-6 gs-shadow mt-6">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Serviço:</span> <b>{lead.service||"—"}</b></div>
            <div><span className="text-slate-500">Cidade:</span> <b>{lead.city||"—"}</b></div>
            <div><span className="text-slate-500">Contato preferido:</span> <b>{lead.preferred_contact||"—"}</b></div>
            <div><span className="text-slate-500">Enviado em:</span> <b>{new Date(lead.created_at).toLocaleString("pt-BR")}</b></div>
          </div>
          <div className="mt-3"><Badge className="bg-[#0D7C66] text-white border-0" data-testid="pedido-status">{lead.status}</Badge></div>
        </Card>

        <div className="mt-8 relative">
          <div className="absolute left-5 top-2 bottom-2 w-0.5 bg-slate-200"/>
          <div className="space-y-5">
            {STEPS.map((s, i)=>{
              const done = i <= currentIdx;
              const Icon = s.icon;
              return (
                <div key={s.key} className="relative flex gap-4" data-testid={`step-${s.key}`}>
                  <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${done?"bg-[#0D7C66] text-white":"bg-slate-100 text-slate-400"}`}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className={`flex-1 rounded-2xl border p-4 ${done?"bg-white border-slate-200":"bg-slate-50 border-slate-100"}`}>
                    <div className={`font-semibold ${done?"text-slate-900":"text-slate-500"}`}>{s.label}</div>
                    <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-[#FEF8F3] p-5 text-sm text-slate-700 border border-[#FF7D54]/20">
          <b>Importante:</b> o GIRO+SAÚDE é uma plataforma de conexão. Não realizamos diagnóstico, prescrição ou atendimento clínico. A responsabilidade pelo atendimento é integralmente do profissional escolhido.
        </div>
      </div>
    </PublicLayout>
  );
}
